/**
 * Die einheitliche Suche ohne KI — `PORTAL-003`.
 *
 * `07` §7.15 führt sie im MVP, ADR-017 hält den Assistenten vollständig hinter
 * dem Piloten. Der Satz aus `CLAUDE.md` dazu ist eindeutig: „Die Suche ohne KI
 * bleibt in Version 1." Sie ist nicht der Platzhalter für den Assistenten,
 * sondern die Antwort auf die Frage nach Auffindbarkeit über alle Systeme.
 *
 * **Was hier zu beurteilen ist, ist die Ergebnisdarstellung, nicht der Treffer.**
 * Der Rumpf in `@hwc/demo-data` sucht mit einer Teilzeichenkette, ohne
 * Gewichtung und ohne Stammformen — `PORTAL-003` ersetzt ihn durch einen Index
 * über die Projektionen (ADR-021). Was bleibt, ist die Frage, ob eine
 * gemischte Trefferliste aus Kunde, Auftrag, Datei, Dokument, Wissensseite und
 * E-Mail überhaupt lesbar ist. Die lässt sich heute beantworten.
 *
 * **Die Gruppierung nach Art ist deshalb kein Layoutgeschmack.** Eine flache
 * Liste, in der ein Kunde zwischen zwei PDF-Dateien steht, zwingt den Leser,
 * jede Zeile einzeln einzuordnen. Nach Art gruppiert überfliegt er die
 * Überschriften.
 */

import { useMemo, useState } from "react";

import { suche } from "@hwc/demo-data";
import type { Treffer } from "@hwc/demo-data";

import { useSitzung } from "../../src/daten/Sitzung.js";
import { Leer, Liste, Zeile, zumAuftrag, zumKunden } from "../../src/bereiche/bausteine.js";

/** Beispiele, die etwas finden. Ein leeres Suchfeld ist eine schlechte Einladung. */
const BEISPIELE = ["Kühlung", "Hufnagel", "Wallbox", "E9", "2481", "Prüfprotokoll"];

/**
 * Wohin ein Treffer führt.
 *
 * Nur Kunde und Auftrag haben im Portal eine eigene Adresse. Alles andere
 * lebt im Quellsystem, und ein Verweis dorthin entsteht erst mit dem
 * jeweiligen Adapter — ein Link, der ins Leere führt, wäre schlechter als
 * keiner. Deshalb gibt diese Funktion `undefined` zurück, wo sie nichts weiß,
 * und die Zeile bleibt eine Zeile statt eines toten Verweises.
 */
function zielFuer(
  treffer: Treffer,
  bestand: ReturnType<typeof useSitzung>["bestand"],
): string | undefined {
  if (treffer.art === "Kunde") {
    const k = bestand.kunden.find((x) => x.ref.opaqueId === treffer.ref.opaqueId);
    return k ? zumKunden(k.nummer) : undefined;
  }
  if (treffer.art === "Auftrag") {
    const a = bestand.auftraege.find((x) => x.ref.opaqueId === treffer.ref.opaqueId);
    return a ? zumAuftrag(a.nummer) : undefined;
  }
  if (treffer.art === "Einsatz") {
    const e = bestand.einsaetze.find((x) => x.ref.opaqueId === treffer.ref.opaqueId);
    const a = e ? bestand.auftraege.find(
      (x) => x.ref.opaqueId === e.auftrag.opaqueId,
    ) : undefined;
    return a ? zumAuftrag(a.nummer) : undefined;
  }
  if (treffer.art === "Eingang") return "/eingang";
  return undefined;
}

/** Die Reihenfolge der Gruppen. Was näher am Auftrag liegt, steht oben. */
const REIHENFOLGE = ["Auftrag", "Kunde", "Einsatz", "Eingang", "Dokument", "Datei", "Wissen", "E-Mail"];

export function Suche() {
  const { bestand } = useSitzung();
  const [frage, setzeFrage] = useState("");

  const gruppen = useMemo(() => {
    const treffer = suche(bestand, frage);
    const nach = new Map<string, Treffer[]>();
    for (const t of treffer) {
      const liste = nach.get(t.art) ?? [];
      liste.push(t);
      nach.set(t.art, liste);
    }
    return [...nach.entries()].sort(
      (a, b) => REIHENFOLGE.indexOf(a[0]) - REIHENFOLGE.indexOf(b[0]),
    );
  }, [bestand, frage]);

  const anzahl = gruppen.reduce((s, [, liste]) => s + liste.length, 0);

  return (
    <>
      <div className="bereichskopf">
        <h1>Suche</h1>
        {frage.trim().length >= 2 && (
          <span className="bereichskopf__zusatz">
            {anzahl} {anzahl === 1 ? "Treffer" : "Treffer"}
          </span>
        )}
      </div>

      <input
        className="suchfeld"
        type="search"
        placeholder="Über Aufträge, Kunden, Dateien, Dokumente, Wissen und E-Mail"
        aria-label="Alles durchsuchen"
        value={frage}
        onChange={(e) => setzeFrage(e.target.value)}
        autoFocus
      />

      {frage.trim().length < 2 ? (
        <>
          <p className="leise">
            Ab zwei Zeichen wird gesucht. Über alle Quellen zugleich — was in Nextcloud,
            Paperless, Vikunja oder Stalwart liegt, erscheint in einer Liste.
          </p>
          <div className="filter">
            {BEISPIELE.map((b) => (
              <button
                key={b}
                type="button"
                className="filter__knopf"
                onClick={() => setzeFrage(b)}
              >
                {b}
              </button>
            ))}
          </div>
          <p className="notiz">
            Ohne KI. Der Assistent liegt nach ADR-017 vollständig hinter dem Piloten;
            bis dahin — und in Version 1 auch danach — deckt diese Suche die
            Auffindbarkeit ab.
          </p>
        </>
      ) : anzahl === 0 ? (
        <Leer text={`Nichts gefunden zu „${frage}".`} />
      ) : (
        gruppen.map(([art, liste]) => (
          <div key={art}>
            <h2>
              {art} <span className="leise">({liste.length})</span>
            </h2>
            <Liste>
              {liste.map((t) => {
                const ziel = zielFuer(t, bestand);
                return (
                  <Zeile
                    key={`${art}-${t.ref.opaqueId}`}
                    zu={ziel}
                    titel={t.titel}
                    unterzeile={t.zusatz}
                    rechts={ziel ? undefined : "im Quellsystem"}
                  />
                );
              })}
            </Liste>
          </div>
        ))
      )}
    </>
  );
}
